import * as React from 'react';
import { useShadowDOM } from '@/common/contexts/ShadowDOMContext';

/**
 * Hook to get the appropriate container for portals (dialogs, dropdowns, tooltips, etc.)
 * 
 * Uses Shadow DOM context to get the portal container when inside a shadow DOM,
 * otherwise falls back to document.body for normal DOM environments.
 * 
 * @param elementRef - Optional ref to an element for edge cases where DOM traversal is needed
 * @returns The appropriate container element (ShadowRoot | HTMLElement)
 * 
 * @example
 * ```tsx
 * const container = usePortalContainer();
 * // Returns portal container from context if in shadow DOM, otherwise document.body
 * ```
 */
export function usePortalContainer(
  elementRef?: React.RefObject<HTMLElement>
): ShadowRoot | HTMLElement {
  const { portalContainer } = useShadowDOM();
  
  // Memoize the container value to prevent recalculation on every render
  // The container value should be stable unless the context actually changes
  return React.useMemo(() => {
    // Primary: use portal container from context (when in shadow DOM)
    if (portalContainer) {
      // CRITICAL: If context provides ShadowRoot, that's a bug - log it clearly
      if (portalContainer instanceof ShadowRoot) {
        console.error('[ShadowDOM] ❌ CRITICAL BUG: Context provided ShadowRoot instead of HTMLElement!');
        console.error('[ShadowDOM] This means ShadowDOMProvider was initialized with ShadowRoot as portalContainer');
        console.error('[ShadowDOM] Stack trace:', new Error().stack);
      }
      
      return portalContainer;
    }
    
    // Fallback: search for global labels-modals portal (common in this extension)
    try {
      const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container') as HTMLElement;
      if (globalPortal) {
        return globalPortal;
      }
    } catch (e) {
      // Ignore errors in global portal detection
    }

    // Fallback: if elementRef provided, try to find shadow root (edge cases)
    if (elementRef?.current) {
      try {
        const root = elementRef.current.getRootNode();
        
        if (root instanceof ShadowRoot) {
          const container = root.querySelector('.btcss-portal-container') as HTMLElement;
          if (container) return container;
          return root;
        }
      } catch (e) {
        console.warn('[ShadowDOM] Error in fallback logic:', e);
      }
    }
    
    return document.body;
  }, [portalContainer, elementRef]);
}

